/**
 * OrganizrContext.tsx - OrganizrContext - React context for Organizr
 * 
 * CHANGE HISTORY:
 * - 251201_1445 danielbeer: Add Actr integration to OrganizrProvider
 * - 251021_0543 [d4517d3] danielbeer: feat: apply performance optimizations to all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { OrganizrManager } from './OrganizrManager';
import { useActr } from '@opcentral/actr';

const OrganizrContext = createContext(null);

export const OrganizrProvider = ({ children, storage = null }) => {
  const managerRef = useRef(null);
  const [, forceUpdate] = useState({});
  const actr = useActr();
  
  // Initialize manager with Actr
  if (!managerRef.current && actr) {
    managerRef.current = new OrganizrManager({ actr, storage });
  }
  
  const manager = managerRef.current;
  
  // Don't render until manager is ready
  if (!manager) {
    return null;
  }
  
  // Subscribe to changes
  useEffect(() => {
    const unsubscribe = manager.subscribe(() => {
      forceUpdate({});
      
      // Auto-save to localStorage
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem('organizr-data', manager.saveToStorage());
        } catch (error) {
          console.error('Failed to save Organizr data:', error);
        }
      }
    });
    
    return unsubscribe;
  }, [manager]);
  
  // Load from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && !storage) {
      try {
        const saved = localStorage.getItem('organizr-data');
        if (saved) {
          manager.loadFromStorage(saved);
          forceUpdate({});
        }
      } catch (error) {
        console.error('Failed to load Organizr data:', error);
      }
    }
  }, [manager, storage]);
  
  // Create stable callbacks
  const createList = useCallback((data) => manager.createList(data), [manager]);
  const getList = useCallback((id) => manager.getList(id), [manager]);
  const getAllLists = useCallback(() => manager.getAllLists(), [manager]);
  const updateList = useCallback((id, updates) => manager.updateList(id, updates), [manager]);
  const deleteList = useCallback((id) => manager.deleteList(id), [manager]);
  const addItem = useCallback((listId, data, parentId) => manager.addItem(listId, data, parentId), [manager]);
  const updateItem = useCallback((listId, itemId, updates) => manager.updateItem(listId, itemId, updates), [manager]);
  const deleteItem = useCallback((listId, itemId) => manager.deleteItem(listId, itemId), [manager]);
  const reorderItems = useCallback((listId, itemIds, parentId) => manager.reorderItems(listId, itemIds, parentId), [manager]);
  const moveItem = useCallback((listId, itemId, newParentId) => manager.moveItem(listId, itemId, newParentId), [manager]);
  const getItemDisplay = useCallback((listId, itemId) => manager.getItemDisplay(listId, itemId), [manager]);
  const getAllItems = useCallback((listId, includeNested) => manager.getAllItems(listId, includeNested), [manager]);
  const createListFromField = useCallback((name, values, options) => manager.createListFromField(name, values, options), [manager]);
  const importList = useCallback((data, format) => manager.importList(data, format), [manager]);
  const exportList = useCallback((listId, format) => manager.exportList(listId, format), [manager]);
  const undo = useCallback(() => manager.undo(), [manager]);
  const redo = useCallback(() => manager.redo(), [manager]);
  const canUndo = useCallback(() => manager.canUndo(), [manager]);
  const canRedo = useCallback(() => manager.canRedo(), [manager]);
  
  const value = useMemo(() => ({
    manager,
    createList,
    getList,
    getAllLists,
    updateList,
    deleteList,
    addItem,
    updateItem,
    deleteItem,
    reorderItems,
    moveItem,
    getItemDisplay,
    getAllItems,
    createListFromField,
    importList,
    exportList,
    undo,
    redo,
    canUndo,
    canRedo
  }), [manager, createList, getList, getAllLists, updateList, deleteList, addItem, updateItem, deleteItem, reorderItems, moveItem, getItemDisplay, getAllItems, createListFromField, importList, exportList, undo, redo, canUndo, canRedo]);
  
  return (
    <OrganizrContext.Provider value={value}>
      {children}
    </OrganizrContext.Provider>
  );
};

export const useOrganizr = () => {
  const context = useContext(OrganizrContext);
  
  if (!context) {
    throw new Error('useOrganizr must be used within OrganizrProvider');
  }
  
  return context;
};

export default OrganizrContext;
